import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { captureException } from '@sentry/react';
import { BankLinkingDialogState } from 'app/pages/cards/credit/BankAccountLinkingDialogs';
import {
  GetPaidolHighnoteIntegrationQuery,
  HighnoteProductType,
  HNAccountHolderApplicationState,
  HNAccountHolderApplicationStatusCode,
  HNAccountHolderCardProductApplicationSnapshotConnection,
  HNAccountHolderVerificationStatusCode,
  HNAccountHolderVerificationStatusReasonCode,
  HNCardProduct,
  HNRecurringAchTransferFrequencyCode,
  HNTransferBalanceAmountCode,
  HNUSBusinessAccountHolder,
  HNUSBusinessAccountHolderSnapshot,
  PaidolHighnoteIntegration,
  PlaidLinkToken,
  PlaidProcessorToken,
  UpdatePaidolHighnoteIntegrationInput,
} from 'API';
import {
  hnSimulateRolloverCurrentFinancialAccountStatement,
  hnSimulateShiftCurrentFinancialAccountStatement,
} from 'app/pages/store/queries';
import { AppDispatch } from 'app/store';
import { signOut, UserAttributes } from 'app/store/authSlice';
import { RootState } from 'app/store/rootReducer';
import { setSelectedPaidolId } from 'app/store/userCompaniesSlice';
import { graphqlOperation } from 'aws-amplify';
// SEE-1232 row p: the superseded Amplify documents (graphql/queries getPaidolHighnoteIntegration,
// graphql_custom/mutations customUpdatePaidolHighnoteIntegration) are left in place, unreferenced.
import { paidolService } from 'app/services/paidol.service';
import { createThrottledThunk } from 'util/thunkHelpers';
import { isPlaidLinkToken, isPlaidProcessorToken } from 'util/typeGuards';
import { graphqlRequest } from 'app/services/middleware';
import { highnoteService } from 'app/services/highnote.service';
import { plaidService } from 'app/services/plaid.service';
import { fetchWithTransportRetry } from 'app/services/networkTransport';

interface Application {
  accountHolderSnapshot?: HNUSBusinessAccountHolderSnapshot;
  applicationState?: HNAccountHolderApplicationState | null;
  cardProduct?: HNCardProduct | null;
  applicationHistory?: HNAccountHolderCardProductApplicationSnapshotConnection | null;
  createdAt?: string | null;
  updatedAt?: string | null;

  needDocuments?: boolean;
}
export interface ReviewOnboardState {
  hasFinancialAccountId?: boolean;
  hasExternalAccountId?: boolean;
  isOnboardedToPcards?: boolean;
  hasSubmittedApplication?: boolean;
  integration?: PaidolHighnoteIntegration;
  plaidLinkToken?: PlaidLinkToken;
  plaidLinkTokenConsumed?: boolean;
  plaidDialogState?: BankLinkingDialogState;
  plaidShouldOpenWhenReady?: boolean;
  plaidProcessorToken?: PlaidProcessorToken;
  application?: Application;
}

export const initialState: ReviewOnboardState = {};

export const getIntegration = createAsyncThunk('reviewOnboard/getIntegration', async (id: string) => {
  return (
    // SEE-1232 row p: transport flip only — same argument, same resolved record.
    paidolService.getPaidolHighnoteIntegration<
      GetPaidolHighnoteIntegrationQuery['getPaidolHighnoteIntegration']
    >(id)
  );
});

export const getCardProductApplication = createAsyncThunk(
  'reviewOnboard/getCardProductApplication',
  async (integration: PaidolHighnoteIntegration, { dispatch }) => {
    const application = (await highnoteService.hnGetAccountHolderCardProductApplication({
      id: integration.accountHolderCardProductApplicationId,
    })) as Application;

    if (!application) {
      return;
    }
    if (
      application?.applicationState?.status === HNAccountHolderApplicationStatusCode.APPROVED &&
      !integration.isApplicationAccepted
    ) {
      dispatch(
        updatePaidolHighnoteIntegration({
          input: { id: integration.id, isApplicationAccepted: true },
        })
      );
    }

    const snapshot = application.accountHolderSnapshot! as HNUSBusinessAccountHolderSnapshot;

    const needBusinessDocuments =
      snapshot.businessProfile?.currentVerification?.status ===
        HNAccountHolderVerificationStatusCode.PENDING &&
      snapshot.businessProfile?.currentVerification.reason ===
        HNAccountHolderVerificationStatusReasonCode.DOCUMENT_UPLOAD_REQUIRED;

    const needPapDocuments =
      snapshot.primaryAuthorizedPerson?.currentVerification?.status ===
        HNAccountHolderVerificationStatusCode.PENDING &&
      snapshot.primaryAuthorizedPerson?.currentVerification.reason ===
        HNAccountHolderVerificationStatusReasonCode.DOCUMENT_UPLOAD_REQUIRED;

    const needBusinessOwnersDocuments = snapshot.businessProfile?.ultimateBeneficialOwners?.some(
      (owner) =>
        owner?.currentVerification?.status === HNAccountHolderVerificationStatusCode.PENDING &&
        owner?.currentVerification?.reason ===
          HNAccountHolderVerificationStatusReasonCode.DOCUMENT_UPLOAD_REQUIRED
    );

    const areDocumentsNeeded = needBusinessDocuments || needPapDocuments || needBusinessOwnersDocuments;

    return { ...application, needDocuments: areDocumentsNeeded };
  }
);

interface senDocumentArgs {
  documentUploadSessionId: string | undefined;
  files: (
    | {
        id: string;
        file: File | undefined;
        documentType?: string | undefined;
      }
    | undefined
  )[];
}

/**
 * A file the browser could not read off disk (SEE-1594).
 *
 * `fetch` with a `File` body rejects with a bare `TypeError` when the OS can no
 * longer serve the bytes the picker snapshotted — the file was moved, renamed,
 * re-synced by OneDrive/iCloud, or locked by another process. Reading it up front
 * turns that into a nameable error we can attribute to a specific file.
 */
export class DocumentFileUnreadableError extends Error {
  readonly fileName: string;

  constructor(fileName: string, cause?: unknown) {
    super(`Could not read "${fileName}" from disk — it may have moved or changed since it was selected.`);
    this.name = 'DocumentFileUnreadableError';
    this.fileName = fileName;
    this.cause = cause instanceof Error ? cause : undefined;
  }
}

/** One or more documents failed to upload after the session had already started. */
export class DocumentUploadFailedError extends Error {
  readonly fileNames: string[];

  constructor(fileNames: string[]) {
    super(`Failed to upload: ${fileNames.join(', ')}`);
    this.name = 'DocumentUploadFailedError';
    this.fileNames = fileNames;
  }
}

/**
 * The session may be stranded IN_PROGRESS — we could not confirm it was closed.
 *
 * Two ways in. `phase: 'end'` is the plain case: the session was started and
 * endDocumentUploadSession failed. `phase: 'start'` is subtler — the start call
 * threw client-side, but highnoteService.execute also throws on a dropped socket,
 * a non-JSON body and any non-2xx, so Highnote may well have started the session
 * before we lost the response.
 *
 * Either way the outcome is the same and it is the worst one: if Highnote did
 * start the session it has emptied primaryDocumentTypes, the page will render no
 * upload controls, and retrying cannot clear it. Recovery needs
 * endDocumentUploadSession run out of band.
 */
export class DocumentSessionStrandedError extends Error {
  readonly documentUploadSessionId?: string;

  readonly phase: 'start' | 'end';

  constructor(phase: 'start' | 'end', documentUploadSessionId?: string, cause?: unknown) {
    super(
      `Document upload session ${
        documentUploadSessionId ?? '(unknown)'
      } may be stranded: ${phase} did not complete.`
    );
    this.name = 'DocumentSessionStrandedError';
    this.documentUploadSessionId = documentUploadSessionId;
    this.phase = phase;
    this.cause = cause instanceof Error ? cause : undefined;
  }
}

// networkTransport's 30s default is sized for API calls. A 10MB document on a
// ~1Mbps mobile uplink needs ~80s, and an abort here costs the customer a full
// re-upload on each of the two retries.
const UPLOAD_TIMEOUT_MS = 180_000;

// Errors crossing a thunk boundary via `.unwrap()` are flattened by
// miniSerializeError into a plain {name, message}, so match on name, not instanceof.
// Same reasoning as isNetworkTransportError in app/services/networkTransport.ts.
function hasErrorName(error: unknown, name: string): boolean {
  if (typeof error !== 'object' || error === null) return false;
  return (error as { name?: unknown }).name === name;
}

export const isDocumentFileUnreadableError = (error: unknown) =>
  hasErrorName(error, 'DocumentFileUnreadableError');

export const isDocumentUploadFailedError = (error: unknown) =>
  hasErrorName(error, 'DocumentUploadFailedError');

export const isDocumentSessionStrandedError = (error: unknown) =>
  hasErrorName(error, 'DocumentSessionStrandedError');

export const sendDocumentForManualReview = createAsyncThunk(
  'reviewOnboard/sendDocumentForManualReview',
  async ({ documentUploadSessionId, files }: senDocumentArgs) => {
    const pending = files.filter((item): item is NonNullable<typeof item> => Boolean(item?.file));
    if (pending.length === 0) return;

    // SEE-1594: prove every file is readable *before* touching Highnote.
    //
    // The failure that caused the incident was a file the OS could no longer
    // serve — moved, renamed or re-synced after the picker snapshotted it. A
    // one-byte read is enough to detect that (blob reads revalidate the snapshot
    // and throw NotReadableError), and failing here leaves the session CREATED,
    // so the customer can re-select and retry with nothing stranded.
    //
    // Only a byte, not the whole file: buffering everything up front would hold
    // every attached file across every concurrent session in memory at once
    // (10MB cap x ~19 possible cards), which OOMs a mobile tab.
    for (const item of pending) {
      try {
        await item.file!.slice(0, 1).arrayBuffer();
      } catch (cause) {
        const error = new DocumentFileUnreadableError(item.file!.name, cause);
        captureException(error);
        throw error;
      }
    }

    try {
      await highnoteService.hnStartDocumentUploadSession({ documentUploadSessionId });
    } catch (cause) {
      // Not simply "the session never started". highnoteService.execute throws on
      // a dropped socket, a non-JSON body and any non-2xx, so Highnote may have
      // started the session and we merely lost the response — in which case it is
      // stranded. Treat the outcome as unknown and say so, rather than telling the
      // customer to retry something that may already be unretryable.
      const error = new DocumentSessionStrandedError('start', documentUploadSessionId, cause);
      captureException(error);
      throw error;
    }

    // Starting the session empties Highnote's primaryDocumentTypes, which is what
    // the upload UI is built from. From here on the session MUST be ended however
    // this block exits — a session left IN_PROGRESS renders the page permanently
    // unusable and needs a manual endDocumentUploadSession to recover (SEE-1594).
    const failed: string[] = [];
    let unexpectedLoopError: unknown;
    try {
      for (const item of pending) {
        const doc = item.file!;
        try {
          // Read one file at a time so only one buffer is live at a time. The
          // bytes still go to fetch as an ArrayBuffer rather than a File, so the
          // upload itself cannot die because the OS moved the file mid-flight.
          const body = await doc.arrayBuffer();

          const createUploadLink = (await highnoteService.hnCreateDocumentUploadLink({
            documentUploadSessionId,
            documentType: item.documentType,
          })) as { uploadUrl?: string } | null;

          const uploadUrl = createUploadLink?.uploadUrl;
          if (!uploadUrl) throw new Error(`Highnote returned no upload URL for ${item.documentType}`);

          // Retrying is safe: a presigned PUT of the same bytes to the same key
          // is idempotent. Without the ok-check a 403 from an expired signature
          // was indistinguishable from a successful upload. The default 30s
          // per-attempt timeout is far too short for a 10MB file on a mobile
          // uplink, so size it for uploads rather than for API calls.
          const response = await fetchWithTransportRetry(
            uploadUrl,
            {
              method: 'PUT',
              body,
              headers: { 'Content-Type': doc.type || 'application/octet-stream' },
            },
            { timeoutMs: UPLOAD_TIMEOUT_MS }
          );
          if (!response.ok) {
            throw new Error(`Upload of ${doc.name} rejected: ${response.status} ${response.statusText}`);
          }
        } catch (error: unknown) {
          // Isolated per file so one bad document cannot strand the others.
          captureException(error);
          failed.push(doc.name);
        }
      }
    } catch (error: unknown) {
      // Nothing in the loop should escape the per-file catch above, but an
      // unexpected throw must not skip the end-session call below. Deliberately
      // not a `finally`: a throw from there would replace this error rather than
      // add to it, which is what the reviewer flagged on PR 2618.
      unexpectedLoopError = error;
    }

    try {
      await highnoteService.hnEndDocumentUploadSession({ documentUploadSessionId });
    } catch (cause) {
      // The one outcome this whole change exists to prevent. Retrying will not
      // help — the session is IN_PROGRESS, so Highnote has already emptied
      // primaryDocumentTypes and the upload controls are gone. Report it above
      // any per-file failure so the user is told to get support rather than to
      // try again. Per-file errors were already sent to Sentry individually.
      const error = new DocumentSessionStrandedError('end', documentUploadSessionId, cause);
      captureException(error);
      throw error;
    }

    if (unexpectedLoopError) throw unexpectedLoopError;

    // Ending with a partial set is the lesser evil, not a good outcome: it is
    // assumed Highnote re-requests whatever is still missing (unverified against
    // the live API — worth confirming). A stranded session is definitely
    // unrecoverable from the UI, so we take the assumption over the certainty.
    if (failed.length > 0) throw new DocumentUploadFailedError(failed);
  }
);

export const getFinancialAccountIdThrottled = (integration: PaidolHighnoteIntegration) =>
  createThrottledThunk(
    (dispatch: AppDispatch) => {
      return dispatch(getFinancialAccountId(integration));
    },
    'reviewOnboard/getFinancialAccountIdThrottled',
    10 * 1000
  );

export const getFinancialAccountId = createAsyncThunk(
  'reviewOnboard/getFinancialAccountId',
  async (integration: PaidolHighnoteIntegration, { dispatch }) => {
    const hnProfile = (await highnoteService.hnGetBusinessAccountHolder({
      id: integration.businessAccountHolderId,
    })) as HNUSBusinessAccountHolder;

    if (hnProfile) {
      const financialAccountId = hnProfile.financialAccounts?.edges?.find((e) => e.node?.id)?.node?.id;
      if (financialAccountId) {
        dispatch(
          updatePaidolHighnoteIntegration({
            input: {
              id: integration.id,
              financialAccountId,
            },
          })
        );
      }
    }
  }
);

export interface GetPlaidLinkTokenArgs {
  userId: string;
  redirectUri: string;
}

export const getPlaidLinkToken = createAsyncThunk(
  'reviewOnboard/getPlaidLinkToken',
  async (args: GetPlaidLinkTokenArgs, { getState }) => {
    // SEE-221: attach the step-up grant (if the session earned one via SMS OTP)
    // so the resolver lets a single-factor session through. SSO/passkey sessions
    // carry no grant and are skipped server-side.
    const stepUpGrant = (getState() as RootState).auth.stepUpGrant;
    return plaidService.createLinkToken({ ...args, stepUpGrant });
  }
);

export interface GetPlaidProcessorTokenArgs {
  publicToken: string;
  accountId: string;
  accountType: string;
}

export const getPlaidProcessorToken = createAsyncThunk(
  'reviewOnboard/getPlaidProcessorToken',
  async ({ publicToken, accountId, accountType }: GetPlaidProcessorTokenArgs) =>
    plaidService.exchangeForProcessorToken({ publicToken, accountId, accountType })
);

// ============================================================================
// NEW SECURE PLAID THUNKS - These keep access_token on the backend
// ============================================================================

/**
 * Link a bank account via Plaid securely.
 * The access_token is stored server-side and never exposed to the frontend.
 */
export interface LinkPlaidBankAccountArgs {
  publicToken: string;
  accountId: string;
  accountType: string;
  accountName: string;
  accountMask: string;
  paidolId: string;
  verificationMethod: 'instant' | 'micro_deposit';
  highnoteAccountId?: string;
}

export interface PlaidLinkedBankAccountSafe {
  id: string;
  paidolId: string;
  plaidAccountId: string;
  plaidItemId: string;
  highnoteAccountId?: string;
  accountName: string;
  accountMask: string;
  accountType: string;
  verificationMethod: string;
  status: string;
  linkedAt: string;
  verifiedAt?: string;
  closedAt?: string;
  failureReason?: string;
}

// The CDK types mark optional fields `T | null`; this module's interface predates
// them and says `T | undefined`. The wire values are the same either way.
const asSafeAccount = (account: unknown) => account as PlaidLinkedBankAccountSafe | undefined;

export const linkPlaidBankAccountSecure = createAsyncThunk(
  'reviewOnboard/linkPlaidBankAccountSecure',
  async (args: LinkPlaidBankAccountArgs) => asSafeAccount(await plaidService.linkBankAccount(args))
);

/**
 * Get PlaidLinkedBankAccounts by paidolId.
 * Returns only safe data - access_token is never exposed.
 */
export interface GetPlaidLinkedBankAccountsArgs {
  paidolId: string;
  status?: string;
}

export const fetchPlaidLinkedBankAccounts = createAsyncThunk(
  'reviewOnboard/fetchPlaidLinkedBankAccounts',
  async ({ paidolId, status }: GetPlaidLinkedBankAccountsArgs) =>
    (await plaidService.listLinkedBankAccounts({ paidolId, status })) as
      | PlaidLinkedBankAccountSafe[]
      | undefined
);

/**
 * Create a verification link token for a pending account.
 * Uses the stored access_token server-side - never exposes it to frontend.
 */
export interface CreateVerificationLinkTokenForAccountArgs {
  accountId: string;
  userId: string;
}

export const createVerificationLinkTokenSecure = createAsyncThunk(
  'reviewOnboard/createVerificationLinkTokenSecure',
  async ({ accountId, userId }: CreateVerificationLinkTokenForAccountArgs, { getState }) => {
    // SEE-221: re-opening Plaid to verify a pending micro-deposit account is
    // gated identically, so it carries the step-up grant too.
    const stepUpGrant = (getState() as RootState).auth.stepUpGrant;
    return plaidService.createVerificationLinkToken({ accountId, userId, stepUpGrant });
  }
);

/**
 * Complete verification and create processor token for a pending account.
 * Returns the processor_token needed for Highnote, but keeps access_token server-side.
 */
export interface CompleteVerificationArgs {
  accountId: string;
  highnoteAccountId?: string;
}

export const completeVerificationSecure = createAsyncThunk(
  'reviewOnboard/completeVerificationSecure',
  async ({ accountId, highnoteAccountId }: CompleteVerificationArgs) =>
    plaidService.completeVerification({ accountId, highnoteAccountId })
);

/**
 * Update the status of a PlaidLinkedBankAccount.
 */
export interface UpdatePlaidLinkedBankAccountStatusArgs {
  id: string;
  status: string;
  highnoteAccountId?: string;
  failureReason?: string;
}

export const updatePlaidLinkedBankAccountStatusSecure = createAsyncThunk(
  'reviewOnboard/updatePlaidLinkedBankAccountStatusSecure',
  async (args: UpdatePlaidLinkedBankAccountStatusArgs) =>
    asSafeAccount(await plaidService.updateLinkedBankAccountStatus(args))
);

// ============================================================================
// END NEW SECURE PLAID THUNKS
// ============================================================================

export interface SimulateCloseStatementArgs {
  financialAccountId: string;
  shiftSecondsAhead?: number; // default 90s
}

// Test-only helper: shifts current statement end boundary slightly forward and rolls over
export const simulateCloseCurrentStatement = createAsyncThunk(
  'reviewOnboard/simulateCloseCurrentStatement',
  async ({ financialAccountId, shiftSecondsAhead = 90 }: SimulateCloseStatementArgs, { dispatch }) => {
    // 1) Shift current statement boundary toward now to avoid overlap rules
    const boundary = new Date(Date.now() + shiftSecondsAhead * 1000).toISOString();
    await graphqlRequest(
      graphqlOperation(hnSimulateShiftCurrentFinancialAccountStatement, {
        input: { financialAccountId, periodBoundary: boundary },
      }),
      dispatch
    );

    // 2) Rollover: closes current and opens next
    await graphqlRequest(
      graphqlOperation(hnSimulateRolloverCurrentFinancialAccountStatement, {
        input: { financialAccountId },
      }),
      dispatch
    );

    return true;
  }
);

export interface CreateOneTimeTransferArgs {
  user: UserAttributes;
  integration: PaidolHighnoteIntegration;
  value: string | HNTransferBalanceAmountCode;
  date: Date;
}

export const createOneTimeTransfer = createAsyncThunk(
  'reviewOnboard/createOneTimeTransfer',
  async ({ user, integration, value, date = new Date() }: CreateOneTimeTransferArgs) => {
    return highnoteService.hnCreateOneTimeACHTransfer({
      transferAmountStrategy: {
        ...(value === HNTransferBalanceAmountCode.OUTSTANDING_BALANCE ||
        value === HNTransferBalanceAmountCode.OUTSTANDING_STATEMENT_BALANCE
          ? { balanceAmountType: value }
          : {
              transferAmount: {
                currencyCode: 'USD',
                value: Math.round(parseFloat(value) * 100),
              },
            }),
      },
      transferDate: new Date(date).toISOString().slice(0, 10),
      transferAgreementConsent: {
        authorizedPersonId: integration.primaryAuthorizedPersonId,
        consentTimestamp: integration.createdAt,
        template: {
          consentTemplateId: process.env.REACT_APP_CONSENT_TEMPLATE_ID,
          consentTemplateVersion: process.env.REACT_APP_CONSENT_TEMPLATE_VERSION,
        },
      },
      descriptor: {
        companyEntryDescription: 'Speedchain',
        individualName: `${user.family_name} ${user.given_name}`.slice(0, 22),
      },
      fromFinancialAccountId: integration.externalFinancialBankAccountId,
      toFinancialAccountId: integration.financialAccountId,
    });
  }
);

export interface CancelScheduledTransferArgs {
  scheduledTransferId: string;
}

export const cancelScheduledTransfer = createAsyncThunk(
  'reviewOnboard/cancelScheduledTransfer',
  async ({ scheduledTransferId }: CancelScheduledTransferArgs) => {
    return highnoteService.hnCancelScheduledTransfer({ scheduledTransferId });
  }
);

export interface CreateRecurringTransferArgs {
  user: UserAttributes;
  integration: PaidolHighnoteIntegration;
  value: string | HNTransferBalanceAmountCode;
  frequency?: HNRecurringAchTransferFrequencyCode;
  transferDayOfMonth?: number;
}

export const createRecurringTransfer = createAsyncThunk(
  'reviewOnboard/createRecurringTransfer',
  async ({
    user,
    integration,
    value,
    frequency = HNRecurringAchTransferFrequencyCode.MONTHLY,
    transferDayOfMonth = 1,
  }: CreateRecurringTransferArgs) => {
    return highnoteService.hnCreateRecurringACHTransfer({
      transferAmountStrategy: {
        ...(value === HNTransferBalanceAmountCode.OUTSTANDING_BALANCE ||
        value === HNTransferBalanceAmountCode.OUTSTANDING_STATEMENT_BALANCE
          ? { balanceAmountType: value }
          : {
              transferAmount: {
                currencyCode: 'USD',
                value: Math.round(parseFloat(value) * 100),
              },
            }),
      },
      frequency,
      transferDayOfMonth,
      transferAgreementConsent: {
        authorizedPersonId: integration.primaryAuthorizedPersonId,
        consentTimestamp: integration.createdAt,
        template: {
          consentTemplateId: process.env.REACT_APP_CONSENT_TEMPLATE_ID,
          consentTemplateVersion: process.env.REACT_APP_CONSENT_TEMPLATE_VERSION,
        },
      },
      descriptor: {
        companyEntryDescription: 'Speedchain',
        individualName: `${user.family_name} ${user.given_name}`.slice(0, 22),
      },
      fromFinancialAccountId: integration.externalFinancialBankAccountId,
      toFinancialAccountId: integration.financialAccountId,
    });
  }
);

export interface InitiateFundsDepositTransferArgs {
  user: UserAttributes;
  integration: PaidolHighnoteIntegration;
  value: number;
  /** Optional: The external bank account ID to transfer from (Plaid accounts).
   * If not provided, falls back to integration.externalFinancialBankAccountId (legacy Finicity). */
  fromAccountId?: string;
}

export const initiateFundsDepositTransfer = createAsyncThunk(
  'reviewOnboard/initiateFundsDepositTransfer',
  async ({
    user,
    integration,
    value,
    fromAccountId,
    sameDay = true,
  }: InitiateFundsDepositTransferArgs & { sameDay?: boolean }) => {
    // Use the provided fromAccountId (Plaid) or fall back to legacy externalFinancialBankAccountId (Finicity)
    const sourceAccountId = fromAccountId || integration.externalFinancialBankAccountId;

    return highnoteService.hnInitiateFundsDepositACHTransfer({
      amount: {
        currencyCode: 'USD',
        value: Math.round(value * 100),
      },
      transferAgreementConsent: {
        authorizedPersonId: integration.primaryAuthorizedPersonId,
        consentTimestamp: integration.createdAt,
        template: {
          consentTemplateId: process.env.REACT_APP_CONSENT_TEMPLATE_ID,
          consentTemplateVersion: process.env.REACT_APP_CONSENT_TEMPLATE_VERSION,
        },
      },
      descriptor: {
        companyEntryDescription: 'Speedchain',
        individualName: `${user.family_name} ${user.given_name}`.slice(0, 22),
      },
      fromFinancialAccountId: sourceAccountId,
      toFinancialAccountId: integration.financialAccountId,
      sameDay,
    });
  }
);

export interface UpdateIntegrationArgs {
  input: UpdatePaidolHighnoteIntegrationInput;
}

export const updatePaidolHighnoteIntegration = createAsyncThunk(
  'reviewOnboard/updatePaidolHighnoteIntegration', // Unique name for the action
  async ({ input }: UpdateIntegrationArgs, { dispatch }) => {
    // SEE-1232 row p: transport flip. CDK does not accept Amplify's `$condition`, which
    // this call never supplied. Empty-string writes are preserved by the CDK resolver —
    // ExternalBankConnection's disconnect sends externalFinancialBankAccountId: ''.
    await paidolService.updatePaidolHighnoteIntegration(input as Record<string, unknown>);

    // Then, dispatch the getIntegration query to fetch the updated data
    if (input.id) {
      return await dispatch(getIntegration(input.id)).unwrap();
    }
  }
);

export const createProductApplication = createAsyncThunk(
  'reviewOnboard/createProductApplication',
  async (
    { id, businessAccountHolderId, primaryAuthorizedPersonId, highnoteProductId }: PaidolHighnoteIntegration,
    { dispatch }
  ) => {
    const result = (await highnoteService.hnCreateAccountHolderCardProductApplication({
      cardProductId: highnoteProductId,
      accountHolderId: businessAccountHolderId,
      cardHolderAgreementConsent: {
        primaryAuthorizedPersonId,
        consentTimestamp: new Date().toISOString(),
      },
    })) as { __typename?: string; id?: string } | null;

    if (result?.__typename === 'HNAccountHolderCardProductApplication') {
      dispatch(
        updatePaidolHighnoteIntegration({
          input: {
            id,
            accountHolderCardProductApplicationId: result.id,
          },
        })
      );

      return result;
    } else {
      throw new Error('Application failed');
    }
  }
);

const reviewOnboardSlice = createSlice({
  name: 'reviewOnboard',
  initialState,
  reducers: {
    resetReviewOnboardSlice: () => initialState,
    setIsOnboardedToPcards: (state, action: PayloadAction<boolean>) => {
      state.isOnboardedToPcards = action.payload;
    },
    setPlaidLinkToken: (state, action: PayloadAction<PlaidLinkToken | null>) => {
      state.plaidLinkToken = action.payload ?? undefined;
      state.plaidLinkTokenConsumed = false;
    },
    consumePlaidLinkToken: (state) => {
      state.plaidLinkTokenConsumed = true;
    },
    setPlaidDialogState: (state, action: PayloadAction<BankLinkingDialogState>) => {
      state.plaidDialogState = action.payload;
    },
    setPlaidShouldOpenWhenReady: (state, action: PayloadAction<boolean>) => {
      state.plaidShouldOpenWhenReady = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPlaidLinkToken.fulfilled, (state, action) => {
      if (isPlaidLinkToken(action.payload)) {
        state.plaidLinkToken = action.payload;
        state.plaidLinkTokenConsumed = false;
      }
    });

    builder.addCase(getPlaidProcessorToken.fulfilled, (state, action) => {
      if (isPlaidProcessorToken(action.payload)) {
        state.plaidProcessorToken = action.payload;
      }
    });

    builder.addCase(createProductApplication.fulfilled, (state) => {
      state.hasSubmittedApplication = true;
    });

    builder.addCase(getCardProductApplication.fulfilled, (state, action) => {
      if (action.payload) {
        state.application = action.payload;
      }
    });

    builder.addCase(updatePaidolHighnoteIntegration.fulfilled, (state, action) => {
      if (action.payload) {
        state.integration = action.payload;
        state.hasExternalAccountId = !!action.payload?.externalFinancialBankAccountId;
      }
    });

    builder.addMatcher(
      isAnyOf(getIntegration.fulfilled, updatePaidolHighnoteIntegration.fulfilled),
      (state, action) => {
        state.integration = action.payload ?? undefined;
        state.hasExternalAccountId = !!action.payload?.externalFinancialBankAccountId;
        state.isOnboardedToPcards =
          !!action.payload?.externalFinancialBankAccountId &&
          (!!action.payload?.hasInitialFunding ||
            action.payload?.highnoteProduct?.type === HighnoteProductType.CREDIT);
        state.hasSubmittedApplication = !!action.payload?.accountHolderCardProductApplicationId;
        state.hasFinancialAccountId = !!action.payload?.financialAccountId;
      }
    );

    builder.addMatcher(
      isAnyOf(setSelectedPaidolId.fulfilled, signOut.fulfilled, getIntegration.pending),
      (state) => {
        Object.assign(state, initialState);
      }
    );
  },
});

export const {
  resetReviewOnboardSlice,
  setIsOnboardedToPcards,
  setPlaidLinkToken,
  consumePlaidLinkToken,
  setPlaidDialogState,
  setPlaidShouldOpenWhenReady,
} = reviewOnboardSlice.actions;

export const selectReviewOnboardSlice = (state: RootState): ReviewOnboardState =>
  state?.cards?.onboard ?? initialState;

export default reviewOnboardSlice.reducer;
